import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL as API } from '../config/api';

const PurchaseNotification = () => {
  const { i18n } = useTranslation();
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const purchaseIndexRef = useRef(0);
  const timeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const isShowingRef = useRef(false);

  // Ref pour la langue courante (évite les re-renders)
  const langRef = useRef(i18n.language);
  useEffect(() => {
    langRef.current = i18n.language;
  }, [i18n.language]);

  // Noms aléatoires pour les notifications (fallback)
  const names = {
    fr: ['Jean-Pierre', 'Marie', 'Pierre', 'Sophia', 'Emmanuel', 'Claudette', 'François', 'Martine', 'Jacques', 'Isabelle', 'Michel', 'Nicole'],
    en: ['John', 'Mary', 'Peter', 'Sarah', 'Michael', 'Jennifer', 'David', 'Lisa', 'James', 'Emily', 'Robert', 'Amanda'],
    ht: ['Jean-Pierre', 'Marie', 'Pierre', 'Sophia', 'Emmanuel', 'Claudette', 'François', 'Martine', 'Jacques', 'Isabelle', 'Michel', 'Nicole'],
    es: ['Juan', 'María', 'Pedro', 'Sofía', 'Manuel', 'Carmen', 'Francisco', 'Martina', 'Carlos', 'Isabel', 'Miguel', 'Ana'],
    pt: ['João', 'Maria', 'Pedro', 'Sofia', 'Manuel', 'Clara', 'Francisco', 'Marta', 'Carlos', 'Isabel', 'Miguel', 'Ana']
  };

  // Messages traduits
  const getMessages = (lang) => ({
    fr: {
      title: 'Nouvel achat !',
      bought: (name, city, count) => `${name} de ${city} vient d'acheter ${count} billet${count > 1 ? 's' : ''}`,
      timeAgo: (time) => `Il y a ${time} minute${time > 1 ? 's' : ''}`
    },
    en: {
      title: 'New purchase!',
      bought: (name, city, count) => `${name} from ${city} just bought ${count} ticket${count > 1 ? 's' : ''}`,
      timeAgo: (time) => `${time} minute${time > 1 ? 's' : ''} ago`
    },
    ht: {
      title: 'Nouvo acha!',
      bought: (name, city, count) => `${name} nan ${city} fèk achte ${count} tikè`,
      timeAgo: (time) => `Sa gen ${time} minit`
    },
    es: {
      title: '¡Nueva compra!',
      bought: (name, city, count) => `${name} de ${city} acaba de comprar ${count} boleto${count > 1 ? 's' : ''}`,
      timeAgo: (time) => `Hace ${time} minuto${time > 1 ? 's' : ''}`
    },
    pt: {
      title: 'Nova compra!',
      bought: (name, city, count) => `${name} de ${city} acabou de comprar ${count} bilhete${count > 1 ? 's' : ''}`,
      timeAgo: (time) => `Há ${time} minuto${time > 1 ? 's' : ''}`
    }
  })[lang] || {
    title: 'Nouvel achat !',
    bought: (name, city, count) => `${name} de ${city} vient d'acheter ${count} billet${count > 1 ? 's' : ''}`,
    timeAgo: (time) => `Il y a ${time} minute${time > 1 ? 's' : ''}`
  };

  const cities = [
    'Port-au-Prince', 'Montréal', 'Miami', 'New York', 'Paris',
    'Cap-Haïtien', 'Toronto', 'Boston', 'Brooklyn', 'Ottawa',
    'Pétion-Ville', 'Jacmel', 'Les Cayes', 'Gonaïves'
  ];

  // Fetch recent purchases (une seule fois)
  useEffect(() => {
    const fetchRecentPurchases = async () => {
      try {
        const response = await axios.get(`${API}/purchases/recent`);
        if (response.data && response.data.length > 0) {
          setRecentPurchases(response.data);
        }
      } catch (error) {
        console.log('Using simulated purchases for notifications');
      }
    };
    fetchRecentPurchases();
  }, []);

  // Fonction pour générer une notification (utilise langRef.current)
  const generateNotification = () => {
    const lang = langRef.current;
    const nameList = names[lang] || names.fr;
    const msg = getMessages(lang);
    
    // Use real purchase if available
    if (recentPurchases.length > 0) {
      const purchase = recentPurchases[purchaseIndexRef.current % recentPurchases.length];
      
      // Use real city if available, otherwise random city
      const city = purchase.city || cities[Math.floor(Math.random() * cities.length)];
      
      let timeAgo = Math.floor(Math.random() * 30) + 1;
      if (purchase.timestamp) {
        const purchaseTime = new Date(purchase.timestamp);
        const now = new Date();
        const diffMinutes = Math.floor((now - purchaseTime) / (1000 * 60));
        timeAgo = Math.max(1, Math.min(diffMinutes, 60));
      }

      purchaseIndexRef.current += 1;
      
      return {
        title: msg.title,
        message: msg.bought(purchase.name || 'Anonymous', city, purchase.ticketCount || 1),
        timeText: msg.timeAgo(timeAgo),
        isReal: true
      };
    }

    // Fallback to simulated notification
    const name = nameList[Math.floor(Math.random() * nameList.length)];
    const city = cities[Math.floor(Math.random() * cities.length)];
    const ticketCount = Math.random() > 0.6 ? 3 : 1;
    const timeAgo = Math.floor(Math.random() * 10) + 1;

    return {
      title: msg.title,
      message: msg.bought(name, city, ticketCount),
      timeText: msg.timeAgo(timeAgo),
      isReal: false
    };
  };

  // Effet principal pour afficher les notifications
  useEffect(() => {
    const showNotification = () => {
      if (isShowingRef.current) return; // Évite les doubles
      isShowingRef.current = true;
      
      const newNotification = generateNotification();
      setNotification(newNotification);
      setIsVisible(true);

      // Hide after 5 seconds
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        isShowingRef.current = false;
        
        // Schedule next notification (random between 20-50 seconds)
        const nextDelay = (Math.floor(Math.random() * 30) + 20) * 1000;
        timeoutRef.current = setTimeout(showNotification, nextDelay);
      }, 5000);
    };

    // Show first notification after 8 seconds
    timeoutRef.current = setTimeout(showNotification, 8000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []); // Pas de dépendances - ne s'exécute qu'une fois

  // Mise à jour du titre quand la langue change (sans recréer la notification)
  const currentTitle = getMessages(i18n.language).title;

  return (
    <AnimatePresence>
      {isVisible && notification && (
        <motion.div
          initial={{ opacity: 0, x: -100, y: 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
          className="fixed bottom-24 left-4 sm:left-6 z-50 max-w-[calc(100vw-2rem)] sm:max-w-sm"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-secondary to-accent p-1"></div>
            <div className="p-3 sm:p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-xs font-medium text-green-600">
                    {currentTitle}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {notification.timeText}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PurchaseNotification;
